import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { Grid, Box, CircularProgress } from "@mui/material";
import { NetworkStatus, useQuery } from "@apollo/client";
import { GET_PROJECTS_BY_STATUS } from "../../graphql/gqlProject";
import ProjectCard from "../../components/ProjectCard";
import { PinwheelLoader } from "../../components/PinwheelLoader";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDeviceSize } from "../../hooks/useDeviceSize";

function chunkArray<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

type Props = { status: "PUBLIC" | "SHARED" | "PRIVATE" };

const PAGE_SIZE = 12;
const ESTIMATED_ROW_HEIGHT = 424;
const SCROLL_THRESHOLD_PX = 2 * ESTIMATED_ROW_HEIGHT;

const ProjectsByStatus: React.FC<Props> = ({ status }) => {
  const parentRef = React.useRef<HTMLDivElement>(null);
  const fetchingRef = useRef(false);
  const shouldResetScrollRef = useRef(true);

  const sizes = useDeviceSize();
  const nrColumns = sizes.xsDevice
    ? 1
    : sizes.smDevice
    ? 2
    : sizes.mdDevice
    ? 3
    : 4;

  const { data, loading, error, fetchMore, refetch, networkStatus } = useQuery(
    GET_PROJECTS_BY_STATUS,
    {
      variables: { status, pagination: { cursor: null, limit: PAGE_SIZE } },
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "network-only",
    }
  );

  const projects = data?.getProjectsByStatus?.data ?? [];
  const hasMore = !!data?.getProjectsByStatus?.hasMore;
  const nextCursor = data?.getProjectsByStatus?.nextCursor ?? null;

  const rows = useMemo(
    () => chunkArray(projects, nrColumns) as any[][],
    [projects, nrColumns]
  );
  const nrRows = rows.length;

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? nrRows + 1 : nrRows,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 6,
  });

  const resetScroll = useCallback(() => {
    if (parentRef.current) {
      parentRef.current.scrollTop = 0;
    }
    rowVirtualizer.scrollToIndex(0);
    rowVirtualizer.measure();
  }, [rowVirtualizer]);

  const triggerFetchMore = useCallback(() => {
    if (!hasMore || loading || !nextCursor || fetchingRef.current) return;
    fetchingRef.current = true;

    fetchMore({
      variables: {
        status,
        pagination: { cursor: nextCursor, limit: PAGE_SIZE },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        fetchingRef.current = false;

        if (!fetchMoreResult?.getProjectsByStatus) return prev;

        const prevList = prev?.getProjectsByStatus?.data ?? [];
        const nextBlock = fetchMoreResult.getProjectsByStatus.data ?? [];
        const merged = [...prevList, ...nextBlock];

        const nextHasMore =
          (fetchMoreResult.getProjectsByStatus.hasMore ?? false) &&
          nextBlock.length > 0;

        return {
          ...prev,
          getProjectsByStatus: {
            __typename:
              prev?.getProjectsByStatus?.__typename ??
              fetchMoreResult.getProjectsByStatus.__typename ??
              "PaginatedProjects",
            data: merged,
            nextCursor: fetchMoreResult.getProjectsByStatus.nextCursor ?? null,
            hasMore: nextHasMore,
          },
        };
      },
    }).catch(() => {
      fetchingRef.current = false;
    });
  }, [hasMore, loading, nextCursor, fetchMore, status]);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    const onScroll = () => {
      if (
        el.scrollTop + el.clientHeight >=
        el.scrollHeight - SCROLL_THRESHOLD_PX
      ) {
        triggerFetchMore();
      }
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [triggerFetchMore]);

  useEffect(() => {
    if (
      networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.refetch
    ) {
      shouldResetScrollRef.current = true;
      return;
    }

    if (networkStatus === NetworkStatus.ready && shouldResetScrollRef.current) {
      resetScroll();
      shouldResetScrollRef.current = false;
    }
  }, [networkStatus, resetScroll]);

  useEffect(() => {
    fetchingRef.current = false;
    shouldResetScrollRef.current = true;
    resetScroll();
    refetch({ status, pagination: { cursor: null, limit: PAGE_SIZE } });
  }, [status, refetch, resetScroll]);

  if (loading && projects.length === 0) return <PinwheelLoader />;
  if (error) throw Error("Projects loading failed");

  return (
    <div
      ref={parentRef}
      style={{
        overflow: "auto",
        height: "100%",
        minHeight: 0,
      }}
    >
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const isLoaderRow = virtualRow.index >= nrRows;
          const row = isLoaderRow ? [] : rows[virtualRow.index];

          return (
            <Box
              key={virtualRow.index}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
                width: "calc(100% - 4rem)",
                px: 4,
              }}
            >
              {isLoaderRow ? (
                hasMore ? (
                  <Box
                    sx={{
                      height: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : null
              ) : (
                <Grid container spacing={3} paddingY={4}>
                  {row.map((project: any) => (
                    <ProjectCard key={project.id} project={project} />
                  ))}
                </Grid>
              )}
            </Box>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectsByStatus;
